export const DEFAULT_SPACING = 20;
export const DEFAULT_SPACING_BOTTOM = 30;

export class Magnetize {
  protected widgetElement: HTMLElement;
  protected offset: {
    bottom: number;
    left?: number;
    right?: number;
  };
  protected resizeObserver: ResizeObserver | null = null;
  protected scrollHandler: (() => void) | null = null;
  protected targetElement: HTMLElement;

  constructor(
    targetElement: HTMLElement,
    widgetElement: HTMLElement,
    offset: {
      bottom: number;
      left?: number;
      right?: number;
    }
  ) {
    this.targetElement = targetElement;
    this.widgetElement = widgetElement;
    this.offset = offset;
  }

  public init() {
    const targetElement = this.targetElement;

    // Initialize ResizeObserver to update position on size changes
    this.resizeObserver = new ResizeObserver(() => {
      this.updatePosition(targetElement);
    });
    this.resizeObserver.observe(targetElement);
    this.resizeObserver.observe(this.widgetElement);

    // Debounce scroll and resize events (~60fps)
    this.scrollHandler = this.debounce(() => {
      this.updatePosition(targetElement);
    }, 16);
    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.scrollHandler, { passive: true });

    // Set initial position
    this.updatePosition(targetElement);
  }

  updatePosition(targetElement: HTMLElement) {
    if (
      !this.isInDocument(targetElement) ||
      targetElement.style.display === 'none'
    ) {
      document.body.style.removeProperty('--rg-magnetize-bottom');

      this.widgetElement.style.position = 'fixed';
      this.widgetElement.style.bottom = `${this.offset.bottom}px`;
      if (this.offset.left !== undefined) {
        this.widgetElement.style.left = `${this.offset.left}px`;
        this.widgetElement.style.right = '';
      } else if (this.offset.right !== undefined) {
        this.widgetElement.style.right = `${this.offset.right}px`;
        this.widgetElement.style.left = '';
      }
      this.cleanup();
      return;
    }

    const targetRect = targetElement.getBoundingClientRect();
    const widgetRect = this.widgetElement.getBoundingClientRect();

    const bottom = window.innerHeight - targetRect.top + this.offset.bottom;

    // Set the CSS variable for use in other components
    document.body.style.setProperty('--rg-magnetize-bottom', `${bottom}px`);

    let left: number | null = null;
    if (this.offset.left !== undefined) {
      left = targetRect.left + this.offset.left;
      if (left < 0) {
        left = 0;
      } else if (left + widgetRect.width > window.innerWidth) {
        left = window.innerWidth - widgetRect.width;
      }
    } else if (this.offset.right !== undefined) {
      left = targetRect.right - widgetRect.width - this.offset.right;
      if (left < 0) {
        left = 0;
      } else if (left + widgetRect.width > window.innerWidth) {
        left = window.innerWidth - widgetRect.width;
      }
    }

    this.widgetElement.style.position = 'fixed';
    this.widgetElement.style.bottom = `var(--rg-magnetize-bottom)`;
    if (left !== null) {
      this.widgetElement.style.left = `${left}px`;
      this.widgetElement.style.right = '';
    }
  }

  protected debounce(func: Function, wait: number) {
    let timeout: number;
    return (...args: any[]) => {
      clearTimeout(timeout);
      timeout = window.setTimeout(() => func.apply(this, args), wait);
    };
  }

  public cleanup() {
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
      this.resizeObserver = null;
    }
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
      window.removeEventListener('resize', this.scrollHandler);
      this.scrollHandler = null;
    }
  }

  public refresh() {
    const targetElement = this.targetElement;
    if (targetElement) {
      this.updatePosition(targetElement);
    }
  }

  protected isInDocument(element) {
    if (document.body.contains(element)) return true;

    let currentElement = element;
    while (currentElement?.parentNode) {
      if (currentElement.parentNode === document) {
        return true;
      } else if (currentElement.parentNode instanceof DocumentFragment) {
        currentElement = currentElement.parentNode.host;
      } else {
        currentElement = currentElement.parentNode;
      }
    }
    return false;
  }
}
